import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { VolunteerAuthProvider } from './contexts/VolunteerAuthContext';
import ProtectedVolRoute from './components/vol/ProtectedVolRoute';
import VolLogin from './pages/vol/VolLogin';
import VolDashboard from './pages/vol/VolDashboard';
import VolProfile from './pages/vol/VolProfile';
import VolPendingApproval from './pages/vol/VolPendingApproval';
import CSVImport from './pages/CSVImport';
import ConfirmationTracking from './pages/ConfirmationTracking';
import DriversAdmin from './pages/DriversAdmin';
import DayOfOperations from './pages/DayOfOperations';
import RosterPage from './pages/RosterPage';
import AvailabilityResponse from './pages/AvailabilityResponse';
import AvailabilityRequests from './pages/AvailabilityRequests';
import DriverAvailabilityResponse from './pages/DriverAvailabilityResponse';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ParkingAttendants from './pages/ParkingAttendants';
import HubOversight from './pages/HubOversight';
import HubCleaning from './pages/HubCleaning';
import HubHospitality from './pages/HubHospitality';

// Pages
import VolunteerForm from './pages/VolunteerForm';
import DriverForm from './pages/DriverForm';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import SchedulingGrid from './pages/SchedulingGrid';
import VolunteerLoad from './pages/VolunteerLoad';
import CheckInInterface from './pages/CheckInInterface';
import CheatSheet from './pages/CheatSheet';
import ConfirmShift from './pages/ConfirmShift';
import BroadcastEmail from './pages/BroadcastEmail';
import SchedulingSettings from './pages/SchedulingSettings';
import VolSchedule from './pages/vol/VolSchedule';

function App() {
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);

  React.useEffect(() => {
    // Check if user is already logged in
    const authStatus = localStorage.getItem('adminAuth');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
    localStorage.setItem('adminAuth', 'true');
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('adminAuth');
  };

  return (
    <Router>
      <VolunteerAuthProvider>
      <div className="App">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/volunteer" element={<VolunteerForm />} />
          <Route path="/driver" element={<DriverForm />} />
          <Route path="/availability" element={<AvailabilityResponse />} />
          <Route path="/driver-availability" element={<DriverAvailabilityResponse />} />
          <Route path="/confirm" element={<ConfirmShift />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          
          {/* Admin Routes */}
          <Route 
            path="/admin/login" 
            element={
              isAuthenticated ? 
              <Navigate to="/admin/dashboard" /> : 
              <AdminLogin onLogin={handleLogin} />
            } 
          />
          <Route 
            path="/admin/dashboard" 
            element={
              isAuthenticated ? 
              <AdminDashboard onLogout={handleLogout} /> : 
              <Navigate to="/admin/login" />
            } 
          />
          <Route
            path="/admin/schedule"
            element={
              isAuthenticated ?
              <SchedulingGrid onLogout={handleLogout} /> :
              <Navigate to="/admin/login" />
            }
          />
          <Route
            path="/admin/volunteer-load"
            element={
              isAuthenticated ?
              <VolunteerLoad onLogout={handleLogout} /> :
              <Navigate to="/admin/login" />
            }
          />
          <Route
            path="/admin/checkin" 
            element={
              isAuthenticated ? 
              <CheckInInterface onLogout={handleLogout} /> : 
              <Navigate to="/admin/login" />
            } 
          />
          <Route 
            path="/admin/settings" 
            element={
              isAuthenticated ? 
              <CSVImport onLogout={handleLogout} /> : 
              <Navigate to="/admin/login" />
            } 
          />
          <Route
            path="/admin/drivers"
            element={
              isAuthenticated ?
              <DriversAdmin onLogout={handleLogout} /> :
              <Navigate to="/admin/login" />
            }
          />
          <Route
            path="/admin/confirmation"
            element={
              isAuthenticated ?
              <ConfirmationTracking onLogout={handleLogout} /> :
              <Navigate to="/admin/login" />
            }
          />
          <Route
            path="/admin/dayof"
            element={
              isAuthenticated ?
              <DayOfOperations onLogout={handleLogout} /> :
              <Navigate to="/admin/login" />
            }
          />
          <Route
            path="/admin/roster"
            element={
              isAuthenticated ?
              <RosterPage onLogout={handleLogout} /> :
              <Navigate to="/admin/login" />
            }
          />
          <Route
            path="/admin/availability"
            element={
              isAuthenticated ?
              <AvailabilityRequests onLogout={handleLogout} /> :
              <Navigate to="/admin/login" />
            }
          />
          <Route
            path="/admin/parking"
            element={
              isAuthenticated ?
              <ParkingAttendants onLogout={handleLogout} /> :
              <Navigate to="/admin/login" />
            }
          />
          <Route
            path="/admin/hub/oversight"
            element={
              isAuthenticated ?
              <HubOversight onLogout={handleLogout} /> :
              <Navigate to="/admin/login" />
            }
          />
          <Route
            path="/admin/hub/cleaning"
            element={
              isAuthenticated ?
              <HubCleaning onLogout={handleLogout} /> :
              <Navigate to="/admin/login" />
            }
          />
          <Route
            path="/admin/hub/hospitality"
            element={
              isAuthenticated ?
              <HubHospitality onLogout={handleLogout} /> :
              <Navigate to="/admin/login" />
            }
          />
          <Route
            path="/admin/cheatsheet"
            element={
              isAuthenticated ?
              <CheatSheet onLogout={handleLogout} /> :
              <Navigate to="/admin/login" />
            }
          />
          <Route
            path="/admin/broadcast"
            element={
              isAuthenticated ?
              <BroadcastEmail onLogout={handleLogout} /> :
              <Navigate to="/admin/login" />
            }
          />
          <Route
            path="/admin/scheduling-settings"
            element={
              isAuthenticated ?
              <SchedulingSettings onLogout={handleLogout} /> :
              <Navigate to="/admin/login" />
            }
          />

          {/* Volunteer Portal Routes */}
          <Route path="/vol/login" element={<VolLogin />} />
          <Route path="/vol/pending" element={<VolPendingApproval />} />
          <Route
            path="/vol/dashboard"
            element={<ProtectedVolRoute><VolDashboard /></ProtectedVolRoute>}
          />
          <Route
            path="/vol/profile"
            element={<ProtectedVolRoute><VolProfile /></ProtectedVolRoute>}
          />
          <Route
            path="/vol/schedule"
            element={<ProtectedVolRoute><VolSchedule /></ProtectedVolRoute>}
          />
          <Route path="/vol" element={<Navigate to="/vol/dashboard" replace />} />
        </Routes>
      </div>
      </VolunteerAuthProvider>
    </Router>
  );
}

// Home Page Component
function Home() {
  return (
    <div className="home-page">
      <div className="container">
        <h1>SMPW Masters Event</h1>
        <h2>Cart Witnessing Volunteer Registration</h2>
        <p className="subtitle">April 9-12, 2026 • Augusta, Georgia</p>
        
        <div className="button-group">
          <a href="/volunteer" className="btn btn-primary">
            Volunteer Registration
          </a>
          <a href="/driver" className="btn btn-secondary">
            Driver/Shuttler Registration
          </a>
        </div>
        
        <div className="admin-link">
          <a href="/admin/login">Admin Login</a>
        </div>
      </div>
    </div>
  );
}

export default App;
